import React, { useState, useEffect } from 'react';
import { emailAPI } from '../api/api';
import { toast } from '../hooks/use-toast';

// ─── Email composer modal ─────────────────────────────────────────────────
// Props:
//   open               — boolean
//   onClose            — () => void
//   onSent             — (message) => void
//   availableDossiers  — [{ id, consultant_name, consultant_title }] selectable as PDF attachments
//   prefill            — { to, subject, body, clientName, tenderId, dossierIds }
const splitEmails = (s) =>
  (s || '')
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter(Boolean);

const EmailComposer = ({ open, onClose, onSent, availableDossiers = [], prefill = {} }) => {
  const [to, setTo] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [clientName, setClientName] = useState('');
  const [attachedIds, setAttachedIds] = useState([]);
  const [picking, setPicking] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTo(prefill.to || '');
    setSubject(prefill.subject || '');
    setBody(prefill.body || '');
    setClientName(prefill.clientName || '');
    setAttachedIds(prefill.dossierIds || []);
    setCc('');
    setShowCc(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const byId = (id) => availableDossiers.find((d) => d.id === id);
  const attached = attachedIds.map(byId).filter(Boolean);
  const notAttached = availableDossiers.filter((d) => !attachedIds.includes(d.id));

  const addDossier = (id) => {
    setAttachedIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    setPicking(false);
  };
  const removeDossier = (id) => setAttachedIds((prev) => prev.filter((x) => x !== id));

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const id = e.dataTransfer.getData('text/dossier-id') || e.dataTransfer.getData('text/plain');
    if (id && byId(id)) addDossier(id);
  };

  const handleSend = async () => {
    const toList = splitEmails(to);
    if (toList.length === 0) {
      toast({ title: 'Destinataire manquant', description: 'Indiquez au moins une adresse.', variant: 'destructive' });
      return;
    }
    setSending(true);
    try {
      const msg = await emailAPI.send({
        to_emails: toList,
        cc_emails: splitEmails(cc),
        subject,
        body_html: body.replace(/\n/g, '<br/>'),
        dossier_ids: attachedIds,
        tender_id: prefill.tenderId || null,
        client_name: clientName || null,
      });
      toast({ title: 'Email envoyé', description: `À ${toList.join(', ')}` });
      onSent?.(msg);
      onClose?.();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Échec de l'envoi.";
      toast({ title: 'Erreur', description: detail, variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '8px 11px', borderRadius: 7,
    background: 'var(--bg)', color: 'var(--white)',
    border: '1px solid var(--border)', outline: 'none', fontSize: 13,
  };
  const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--muted2)', marginBottom: 4, display: 'block' };

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 580, maxHeight: '90vh', overflowY: 'auto',
          background: 'var(--panel, #16161d)', border: '1px solid var(--border)',
          borderRadius: 14, boxShadow: '0 24px 60px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <span style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,var(--vio),#2563EB)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><rect x="1.5" y="3" width="13" height="10" rx="2"/><path d="M2 4l6 4.5L14 4"/></svg>
            </span>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--white)' }}>Nouvel email au client</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--muted2)', cursor: 'pointer', fontSize: 20, lineHeight: 1 }}>×</button>
        </div>

        {/* Body */}
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 13 }}>
          <div>
            <label style={labelStyle}>À</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input style={inputStyle} value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@entreprise.com" />
              {!showCc && (
                <button onClick={() => setShowCc(true)} style={{ ...inputStyle, width: 'auto', whiteSpace: 'nowrap', cursor: 'pointer', color: 'var(--vio2)', fontWeight: 600 }}>Cc</button>
              )}
            </div>
          </div>

          {showCc && (
            <div>
              <label style={labelStyle}>Cc</label>
              <input style={inputStyle} value={cc} onChange={(e) => setCc(e.target.value)} placeholder="collègue@entreprise.com" />
            </div>
          )}

          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ flex: 2 }}>
              <label style={labelStyle}>Objet</label>
              <input style={inputStyle} value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Proposition de profils — …" />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Client</label>
              <input style={inputStyle} value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Nom du client" />
            </div>
          </div>

          <div>
            <label style={labelStyle}>Message</label>
            <textarea
              style={{ ...inputStyle, minHeight: 130, resize: 'vertical', lineHeight: 1.5 }}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder={"Bonjour,\n\nVeuillez trouver ci-joint les dossiers de compétences des consultants proposés…"}
            />
          </div>

          {/* Attachments — drag dossiers here */}
          <div>
            <label style={labelStyle}>Dossiers joints (PDF générés automatiquement)</label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              style={{
                border: `1.5px dashed ${dragOver ? 'var(--vio)' : 'var(--border)'}`,
                background: dragOver ? 'rgba(123,104,238,0.06)' : 'transparent',
                borderRadius: 9, padding: 12, transition: 'all .15s',
              }}
            >
              {attached.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)', textAlign: 'center', padding: '6px 0' }}>
                  Glissez des dossiers ici, ou
                  <button onClick={() => setPicking((v) => !v)} style={{ marginLeft: 5, color: 'var(--vio2)', background: 'transparent', border: 'none', cursor: 'pointer', fontWeight: 600 }}>+ Ajouter un dossier</button>
                </div>
              )}
              {attached.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {attached.map((d) => (
                    <span key={d.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(123,104,238,0.12)', color: 'var(--vio2)', border: '1px solid rgba(123,104,238,0.3)', borderRadius: 7, padding: '5px 9px', fontSize: 12, fontWeight: 600 }}>
                      <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.4"><path d="M3 1.5h4L9.5 4v6.5a1 1 0 01-1 1h-5a1 1 0 01-1-1v-8a1 1 0 011-1z"/></svg>
                      {d.consultant_name || 'Dossier'}
                      <button onClick={() => removeDossier(d.id)} style={{ background: 'transparent', border: 'none', color: 'var(--vio2)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                  <button onClick={() => setPicking((v) => !v)} style={{ border: '1px dashed var(--border)', background: 'transparent', color: 'var(--muted2)', borderRadius: 7, padding: '5px 9px', fontSize: 12, cursor: 'pointer' }}>+ Ajouter</button>
                </div>
              )}

              {picking && notAttached.length > 0 && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--border)', paddingTop: 8, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {notAttached.map((d) => (
                    <button
                      key={d.id}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData('text/dossier-id', d.id)}
                      onClick={() => addDossier(d.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 8, textAlign: 'left', background: 'transparent', border: 'none', color: 'var(--white)', padding: '6px 8px', borderRadius: 6, cursor: 'pointer', fontSize: 12.5 }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.04)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <span style={{ color: 'var(--muted)' }}>⠿</span>
                      <span style={{ fontWeight: 600 }}>{d.consultant_name || 'Dossier'}</span>
                      <span style={{ color: 'var(--muted)', fontSize: 11 }}>{d.consultant_title || ''}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 18px', borderTop: '1px solid var(--border)' }}>
          <button onClick={onClose} disabled={sending} style={{ padding: '9px 16px', borderRadius: 8, background: 'transparent', color: 'var(--muted2)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Annuler</button>
          <button onClick={handleSend} disabled={sending} style={{ padding: '9px 18px', borderRadius: 8, background: 'linear-gradient(135deg,var(--vio),#2563EB)', color: '#fff', border: 'none', cursor: sending ? 'default' : 'pointer', fontSize: 13, fontWeight: 700, opacity: sending ? 0.7 : 1, display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14.5 1.5L7 9M14.5 1.5l-4.5 13-2.5-5.5L2 6z"/></svg>
            {sending ? 'Envoi…' : 'Envoyer'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
